/**
 * Rows-driven chart config — the user-level counterpart to `DATA_PRESETS`.
 *
 * A preset supplies a `GraphSpecNode[]` (yAxis + data-series + renderer nodes)
 * keyed by lookupKey. When NO preset matches, the same chart config can live in
 * the sheet's OWN rows: a `data-canvas` layout row whose children are
 * `canvas.*` renderer rows (key/color attrs) and `data-series` rows (formula,
 * seriesKey, `dataSource:"formula"`). This module bridges those rows into the
 * identical `GraphSpecNode` shape `propsFromNodes` already walks, so the engine
 * carries no domain knowledge of any particular chart — the rows do.
 *
 * Topology: the `DataFacade` lives on the data SOURCE row (cells + lookupKey).
 * The `data-canvas` layout row carries the chart config as its subtree. Two
 * supported layouts:
 *   - the canvas is a CHILD of the source (`data` row → `data-canvas` →
 *     renderer/series rows); or
 *   - the source is nested UNDER the canvas (subsumed, mirroring the
 *     preset-expansion output) so the canvas is the source's parent.
 * Either way the canvas subtree is the config and the source contributes only
 * its cells.
 */

import type { Option } from "../../core/option.ts";
import { Json } from "../../core/json.ts";
import { Tracing } from "@tento-core/tracing";

import type { ModelRow } from "../../rows/model-row.ts";
import type { GraphSpecNode } from "./graph-props.ts";

/**
 * The chart-config node tree for a data source row whose sheet carries the
 * config in its own rows — `[data-canvas]` when a `data-canvas` layout row sits
 * directly under OR directly over the source, else `null` (no rows-driven
 * config; the caller falls back to the empty index).
 */
export function dataCanvasGraphNodes(
  sourceRow: ModelRow,
): Option<readonly GraphSpecNode[]> {
  const found = findDataCanvas(sourceRow);
  // One-shot per refresh — did we find a canvas layout row, and via which
  // orientation, plus what the canvas's children look like after wire-type
  // recovery. This is where a mis-recovered descriptor (e.g. a canvas row that
  // hydrated as a fallback source, or a data-series row not recognized) shows up.
  Tracing.log("rows-chart/find-canvas", {
    sourceRowId: sourceRow.id.toString(),
    orientation: found?.orientation ?? "none",
    canvasRowId: found?.canvas.id.toString() ?? null,
    childCount: found?.canvas.children.length ?? 0,
    children: (found?.canvas.children ?? []).map((c) =>
      `${graphNodeType(c)}[${c.descriptor.role}]`
    ),
  });
  return found == null ? null : [toGraphNode(found.canvas)];
}

function findDataCanvas(
  sourceRow: ModelRow,
): Option<{ canvas: ModelRow; orientation: "child-of" | "parent-of" }> {
  for (const child of sourceRow.children) {
    if (isDataCanvas(child)) return { canvas: child, orientation: "child-of" };
  }
  const parent = sourceRow.parent;
  if (parent != null && isDataCanvas(parent)) {
    return { canvas: parent, orientation: "parent-of" };
  }
  return null;
}

function isDataCanvas(row: ModelRow): boolean {
  const d = row.descriptor;
  return d.role === "layout" && d.layout === "canvas";
}

function toGraphNode(row: ModelRow): GraphSpecNode {
  const formula = rowFormula(row);
  const label = row.label;
  const attributes = row.attributes();
  const graph = row.graphHooks;
  return {
    type: graphNodeType(row),
    ...(label != null ? { label } : {}),
    ...(attributes != null ? { attributes } : {}),
    ...(graph != null ? { graph } : {}),
    children: row.children.map(toGraphNode),
    ...(formula !== null ? { formula } : {}),
  };
}

/**
 * The `propsFromNodes`-facing wire type for a row, recovered from its
 * descriptor: a `data-canvas` layout, a renderer's qualified id
 * (`canvas.line`), or an obj source's precise subtype (`data-series` /
 * `data-json`). Any other row returns its role — a benign string
 * `propsFromNodes` ignores.
 */
function graphNodeType(row: ModelRow): string {
  const d = row.descriptor;
  if (d.role === "renderer") return d.rendererId as string;
  if (d.role === "layout") return d.layout === "canvas" ? "data-canvas" : d.layout;
  if (d.role === "source" && d.dtype === "obj" && d.objSubtype !== undefined) {
    return d.objSubtype;
  }
  return d.role;
}

/**
 * The Lisp expression a `data-series` row carries — stored in the row's
 * `formula` metadata column (the preset carries it as `spec.formula`). Falls
 * back to an `attributes.formula` string for rows that stash it there.
 */
function rowFormula(row: ModelRow): Option<string> {
  const meta = row.sheetReads.rowMeta(row.id).formula;
  if (typeof meta === "string" && meta.length > 0) return meta;
  const attr = Json.string(row.attributes(), "formula");
  return attr != null && attr.length > 0 ? attr : null;
}
